/**
 * The layout every full-page form uses — the Marketplace admin portal's form (2026-09-28, the project
 * owner's reference for every back-office form): the page header, then the form's `FormGroup`
 * sections straight on the page, each a title over its fields and separated by a dashed divider,
 * and `FormFooter` as a sticky bar spanning the whole content column at the bottom of the screen.
 *
 * Taken from the template's own pieces rather than invented:
 * - the dashed section stack is vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/
 *   personal-info.tsx's `divide-y divide-dashed` wrapper;
 * - the footer is `FormFooter` itself: its negative side margins are exactly `HydrogenLayout`'s page
 *   gutter, so with no card around the form the bar runs edge to edge of the content column. It is
 *   `sticky bottom-0` inside its form: pinned to the bottom of the screen while a long form scrolls.
 * - the bar is ALWAYS at the bottom of the screen, however few fields the form has: `HydrogenLayout`
 *   grows the page to the column's height, and a form that is its page's last element grows into the
 *   rest, pushes the bar to its end (`mt-auto`) and takes back the page's bottom padding (the
 *   layout's own `pb-*` scale) so the bar sits flush with the screen's edge. "Last" ignores a
 *   trailing `<dialog>` — `ReasonDialog` is a native dialog, in the DOM even while closed. A form with
 *   real content after it is not last, so its bar rests at the form's end and that content still
 *   follows it rather than sliding under it; a page puts a secondary form ABOVE its main one for that
 *   reason (the category page's Move).
 *
 * Usage: `<Form className={formCard}>` → `<div className={formSections}>…FormGroups…</div>` →
 * `<FormFooter className={formCardFooter} … />`. The names are kept from when the form sat in a card.
 */
export const formCard =
  "@container flex flex-col [&:not(:has(~:not(dialog)))]:grow [&:not(:has(~:not(dialog)))]:-mb-6 lg:[&:not(:has(~:not(dialog)))]:-mb-8 4xl:[&:not(:has(~:not(dialog)))]:-mb-9";

/**
 * A secondary form on the same page (one `<form>` cannot nest in another), placed above the main
 * form and divided from it like one more section.
 */
export const formCardPadded = "@container border-b border-dashed border-muted pb-7";

export const formSections =
  "mb-6 grid gap-7 divide-y divide-dashed divide-muted [&>*:not(:first-child)]:pt-7";

/** `FormFooter` at the form's end — the bottom of the screen, when the form fills the page. */
export const formCardFooter = "mb-0 mt-auto";
